import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  companiesService,
  Company,
  evoWebhookConfigService,
  EvoWebhookConfig,
  evoRemoteWebhooksService,
  EvoRemoteWebhook,
  EvoReedyEventType,
  evoPermissionCheckService,
} from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';
import DevModePanel from '../components/evo/DevModePanel';
import LoadingOverlay from '../components/LoadingOverlay';
import DestructiveActionDialog from '../components/DestructiveActionDialog';
import { StatusChip, StatusTone, StepHint, StepSection } from '../components/evo/EvoStepSection';
import EvoWebhookCredentialsDialog, { EvoCredentialsProof, EvoCredentialsValues } from '../components/evo/EvoWebhookCredentialsDialog';
import EvoPermissionCheckDialog, { CopyButton, PermissionCheckRow, PermissionCheckStepList } from '../components/evo/EvoPermissionCheckDialog';
import { getLeftoverNotice, getPermissionCheckState, maskDns } from '../components/evo/evoPermissionCheckStatus';
import { EvoWebhookCreateDialog, EvoWebhookProfileDialog } from '../components/evo/EvoWebhookDialogs';
import { analyzeReedyWebhooks, EvoWebhookRowStatus } from '../components/evo/evoWebhookStatus';

const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
const WEBHOOK_HANDLER_BASE = `https://us-central1-${projectId}.cloudfunctions.net/evoWebhookHandler`;

const NO_PERMISSION_HINT = 'Você não tem permissão de edição no módulo Webhooks EVO.';
const EVO_CHECK_FAILED_HINT = 'Não foi possível verificar os webhooks na EVO';
const EVO_CHECKING_HINT = 'Consultando a EVO…';

type ListStatus = 'idle' | 'loading' | 'ready' | 'failed';

interface InlineFeedback {
  severity: 'error' | 'warning';
  message: string;
  conflictingWebhookConfigId?: string;
}

const rowStatusChip: Record<EvoWebhookRowStatus, { label: string; tone: StatusTone }> = {
  created: { label: 'Criado', tone: 'ok' },
  missing: { label: 'Faltando', tone: 'neutral' },
  duplicate: { label: 'Duplicado', tone: 'warning' },
};

const KeyValue: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <Box component="dt" sx={{ color: 'text.secondary' }}>
      {label}
    </Box>
    <Box component="dd" sx={{ m: 0, minWidth: 0, overflowWrap: 'anywhere' }}>
      {children}
    </Box>
  </>
);

const KeyValueList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="dl"
    sx={{
      m: 0,
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 200px) 1fr' },
      gap: { xs: 0.25, sm: 1 },
      columnGap: { sm: 2 },
      alignItems: 'center',
      typography: 'body2',
      '& dd': { mb: { xs: 1, sm: 0 } },
    }}
  >
    {children}
  </Box>
);

const DisabledTooltip: React.FC<{ title: string; children: React.ReactElement }> = ({ title, children }) => (
  <Tooltip title={title}>
    <span>{children}</span>
  </Tooltip>
);

const EvoWebhookDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('webhooks_evo', 'editar');
  const { id } = useParams<{ id: string }>();

  const [config, setConfig] = useState<EvoWebhookConfig | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [allEvoConfigs, setAllEvoConfigs] = useState<EvoWebhookConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const loadRequestRef = useRef(0);

  const [webhooks, setWebhooks] = useState<EvoRemoteWebhook[]>([]);
  const [listStatus, setListStatus] = useState<ListStatus>('idle');
  const listRequestRef = useRef(0);

  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const [removing, setRemoving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [createFeedback, setCreateFeedback] = useState<InlineFeedback | null>(null);
  const [dangerError, setDangerError] = useState('');

  const [changingCompany, setChangingCompany] = useState(false);
  const [companyLinkDraft, setCompanyLinkDraft] = useState('');
  const [savingCompanyLink, setSavingCompanyLink] = useState(false);
  const [companyLinkError, setCompanyLinkError] = useState<InlineFeedback | null>(null);

  // Teste de permissões do token (REEDY-141): diálogo do J1, "Ver resultado" e aviso de sobra.
  const [checkOpen, setCheckOpen] = useState(false);
  const [resultToggle, setResultToggle] = useState<boolean | null>(null);
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState('');

  const [devModeOpen, setDevModeOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const notify = (message: string, severity: 'success' | 'warning' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchRemoteWebhooks = useCallback(
    async (cfg: EvoWebhookConfig) => {
      const requestId = ++listRequestRef.current;
      if (!id || !cfg.dns || !cfg.token || !cfg.companyId) {
        setWebhooks([]);
        setListStatus('idle');
        return;
      }
      setListStatus('loading');
      try {
        const list = await evoRemoteWebhooksService.list(id);
        if (requestId !== listRequestRef.current) return;
        setWebhooks(list);
        setListStatus('ready');
      } catch (error) {
        if (requestId !== listRequestRef.current) return;
        console.error('Error loading remote webhooks:', error);
        setWebhooks([]);
        setListStatus('failed');
      }
    },
    [id]
  );

  // Carregamento inicial (coberto pelo overlay). A lista da EVO carrega depois, com esqueleto na etapa 3.
  const loadAll = useCallback(async () => {
    if (!id) return;
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    setLoadFailed(false);
    try {
      const [configData, companiesData, evoConfigsData] = await Promise.all([
        evoWebhookConfigService.getById(id),
        companiesService.getAll(),
        evoWebhookConfigService.getAll(),
      ]);
      // O overlay não cancela a requisição: ignora resposta de tentativa anterior.
      if (requestId !== loadRequestRef.current) return;
      setConfig(configData);
      setCompanies(companiesData);
      setAllEvoConfigs(evoConfigsData);
      setCompanyLinkDraft(configData.companyId || '');
      setChangingCompany(false);
      setLoading(false);
      void fetchRemoteWebhooks(configData);
    } catch (error) {
      if (requestId !== loadRequestRef.current) return;
      console.error('Error loading config:', error);
      setLoadFailed(true);
      setLoading(false);
    }
  }, [id, fetchRemoteWebhooks]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Refetch depois de uma ação: sem overlay.
  const refreshConfigAndList = async () => {
    if (!id) return;
    try {
      const cfg = await evoWebhookConfigService.getById(id);
      setConfig(cfg);
      setResultToggle(null);
      await fetchRemoteWebhooks(cfg);
    } catch (error) {
      console.error('Error refreshing config:', error);
      notify('Não foi possível atualizar a tela. Recarregue a página.', 'warning');
    }
  };

  // Só o cadastro (resultado do teste e aviso de sobra): o teste não muda os webhooks Reedy, então a lista da EVO não é relida.
  const reloadConfig = async () => {
    if (!id) return;
    try {
      const cfg = await evoWebhookConfigService.getById(id);
      setConfig(cfg);
      setResultToggle(null);
    } catch (error) {
      console.error('Error reloading config:', error);
      notify('Não foi possível atualizar a tela. Recarregue a página.', 'warning');
    }
  };

  const errorMessage = (error: unknown, fallback: string) => (error instanceof Error && error.message ? error.message : fallback);

  if (loading || loadFailed) {
    return <LoadingOverlay loading={loading} error={loadFailed} onRetry={loadAll} onBack={() => navigate('/evo-webhooks')} />;
  }

  if (!config || !id) {
    return (
      <Box p={3}>
        <Alert severity="error">Cadastro não encontrado</Alert>
      </Box>
    );
  }

  // ── Estado derivado ────────────────────────────────────────────────────────
  const hasCredentials = Boolean(config.dns && config.token);
  const hasCompany = Boolean(config.companyId);
  const nativeToken = config.nativeIntegration === true;
  const linkedCompany = companies.find((company) => company.id === config.companyId);
  const linkedCompanyUnknown = hasCompany && !linkedCompany;
  const analysis = analyzeReedyWebhooks(webhooks, config.companyId);
  const hasReedyWebhooks = analysis.reedy.length > 0;
  const listLoading = listStatus === 'loading';
  const listFailed = listStatus === 'failed';
  const listReady = listStatus === 'ready';
  const hasDuplicates = listReady && analysis.duplicateEvents.length > 0;
  const missingCount = analysis.missingEvents.length;
  const companyLabel = linkedCompany?.name || config.companyId || '—';
  const callbackUrl = config.companyId ? `${WEBHOOK_HANDLER_BASE}?id=${encodeURIComponent(config.companyId)}` : '';
  const missingPrerequisites = [...(hasCredentials ? [] : ['Cadastrar as credenciais']), ...(hasCompany ? [] : ['Vincular uma empresa'])];
  const createdAtSeconds = config.createdAt?.seconds;
  const createdAtLabel = createdAtSeconds ? new Date(createdAtSeconds * 1000).toLocaleDateString('pt-BR') : '';
  const notes = (config.notes || '').trim();
  const checkState = getPermissionCheckState(config.permissionCheck);
  const resultOpen = resultToggle ?? checkState.autoOpen;
  const savedCheckRows: PermissionCheckRow[] = (config.permissionCheck?.steps ?? []).map((step) => ({
    id: step.id,
    status: step.status,
    httpStatus: step.httpStatus,
    message: step.message,
  }));
  const leftoverNotice = getLeftoverNotice(config.permissionCheckLeftover, config.dns);

  const generalChip: { label: string; tone: StatusTone } = !hasCredentials || !hasCompany
    ? { label: 'Configuração incompleta', tone: 'neutral' }
    : listLoading || listStatus === 'idle'
      ? { label: EVO_CHECKING_HINT, tone: 'neutral' }
      : listFailed
        ? { label: 'Falha ao consultar a EVO', tone: 'error' }
        : hasDuplicates
          ? { label: 'Webhooks duplicados', tone: 'warning' }
          : missingCount === 0
            ? { label: 'Webhooks ativos', tone: 'ok' }
            : missingCount === 3
              ? { label: 'Sem webhooks na EVO', tone: 'neutral' }
              : { label: `Webhooks incompletos: ${3 - missingCount} de 3`, tone: 'warning' };

  const step3Chip: { label: string; tone: StatusTone } = missingPrerequisites.length > 0
    ? { label: 'Aguardando etapas 1 e 2', tone: 'neutral' }
    : listLoading || listStatus === 'idle'
      ? { label: EVO_CHECKING_HINT, tone: 'neutral' }
      : listFailed
        ? { label: 'Falha ao consultar', tone: 'error' }
        : hasDuplicates
          ? { label: `Revisar: ${analysis.reedy.length} encontrados`, tone: 'warning' }
          : missingCount === 0
            ? { label: '3 de 3 ativos', tone: 'ok' }
            : missingCount === 3
              ? { label: 'Nenhum criado', tone: 'neutral' }
              : { label: `${3 - missingCount} de 3 ativos`, tone: 'warning' };

  const step1State = hasCredentials ? 'done' : 'current';
  const step2Ready = hasCompany && !linkedCompanyUnknown;
  const step2State = step2Ready ? 'done' : hasCredentials ? 'current' : 'off';
  const step3State = missingPrerequisites.length > 0 || !step2Ready ? 'off' : listReady && !hasDuplicates && missingCount === 0 ? 'done' : 'current';

  // Trava de DNS, token, tipo do token e empresa: webhooks Reedy ativos (regra existente).
  const credentialsLocked = hasReedyWebhooks;
  const companyChangeBlockedReason = listFailed
    ? EVO_CHECK_FAILED_HINT
    : listLoading
      ? EVO_CHECKING_HINT
      : hasReedyWebhooks
        ? 'Para trocar a empresa, remova antes os webhooks Reedy.'
        : '';
  const companyEditing = !hasCompany || linkedCompanyUnknown || changingCompany;
  const companyLinkDirty = companyLinkDraft !== (config.companyId || '');

  const occupiedCompanyIds = new Set(allEvoConfigs.filter((c) => c.id !== id && c.companyId).map((c) => c.companyId as string));
  const companiesForSelect = companies.filter((company) => !occupiedCompanyIds.has(company.id) || company.id === config.companyId);
  companiesForSelect.sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return a.name.localeCompare(b.name, 'pt-BR');
  });

  // ── Ações ──────────────────────────────────────────────────────────────────
  const submitCredentials = async (values: EvoCredentialsValues, proof: EvoCredentialsProof) => {
    try {
      const result = await evoWebhookConfigService.updateCredentials(id, { ...values, permissionCheckRunId: proof.runId });
      if (result.success) {
        setCredentialsOpen(false);
        notify(`Credenciais salvas. Permissões verificadas: ${proof.summary}.`);
        void refreshConfigAndList();
        return { success: true };
      }
      return {
        success: false,
        error: result.error || 'Erro ao salvar credenciais',
        conflictingWebhookConfigId: result.conflictingWebhookConfigId,
      };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Erro ao salvar credenciais') };
    }
  };

  const submitProfile = async (values: { name: string; notes: string }) => {
    try {
      const result = await evoWebhookConfigService.update(id, values);
      if (result.success && result.data) {
        setConfig(result.data);
        setProfileOpen(false);
        notify('Nome e observações atualizados.');
        return { success: true };
      }
      return { success: false, error: result.error || 'Erro ao salvar' };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'Erro ao salvar') };
    }
  };

  const handleSaveCompanyLink = async () => {
    if (!companyLinkDirty) return;
    setSavingCompanyLink(true);
    setCompanyLinkError(null);
    try {
      const result = await evoWebhookConfigService.update(id, {
        companyId: companyLinkDraft.trim() === '' ? null : companyLinkDraft.trim(),
      });
      if (result.success && result.data) {
        setConfig(result.data);
        setCompanyLinkDraft(result.data.companyId || '');
        setChangingCompany(false);
        notify('Vínculo com empresa atualizado.');
        const configsData = await evoWebhookConfigService.getAll();
        setAllEvoConfigs(configsData);
        await fetchRemoteWebhooks(result.data);
      } else {
        setCompanyLinkError({
          severity: 'error',
          message: result.error || 'Erro ao salvar vínculo',
          conflictingWebhookConfigId: result.conflictingWebhookConfigId,
        });
      }
    } catch (error) {
      setCompanyLinkError({ severity: 'error', message: errorMessage(error, 'Erro ao salvar vínculo') });
    } finally {
      setSavingCompanyLink(false);
    }
  };

  const handleConfirmCreate = async () => {
    // Proteção de duplo clique na própria tela (não impede outra aba ou outro admin: ver spec A5).
    if (creatingRef.current || !hasCompany) return;
    creatingRef.current = true;
    setCreating(true);
    setCreateFeedback(null);
    try {
      const result = await evoRemoteWebhooksService.createAll(id);
      setCreateOpen(false);
      if (result.success) {
        notify(result.message || 'Webhooks criados na EVO.', result.data?.verified === false ? 'warning' : 'success');
      } else {
        const softCode = result.code === 'duplicates-detected' || result.code === 'legacy-duplicates';
        setCreateFeedback({
          severity: softCode ? 'warning' : 'error',
          message: result.error || 'Erro ao criar webhooks',
        });
      }
      // A tela mostra o estado real da EVO, inclusive depois de erro parcial ou duplicado.
      await fetchRemoteWebhooks(config);
    } catch (error) {
      setCreateOpen(false);
      setCreateFeedback({ severity: 'error', message: errorMessage(error, 'Erro ao criar webhooks') });
      await fetchRemoteWebhooks(config);
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  const handleConfirmRemove = async () => {
    setRemoving(true);
    setDangerError('');
    try {
      const result = await evoRemoteWebhooksService.deleteAll(id);
      setRemoveOpen(false);
      if (result.success) {
        const warnings = result.data?.warnings;
        const message = warnings?.length && (result.data?.deleted ?? 0) > 0
          ? `${result.message || 'Concluído.'} Avisos: ${warnings.join('; ')}`
          : result.message || 'Webhooks removidos.';
        notify(message, warnings?.length ? 'warning' : 'success');
      } else {
        setDangerError(result.error || 'Erro ao remover webhooks');
      }
      setCreateFeedback(null);
      await fetchRemoteWebhooks(config);
    } catch (error) {
      setRemoveOpen(false);
      setDangerError(errorMessage(error, 'Erro ao remover webhooks'));
    } finally {
      setRemoving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    setDangerError('');
    try {
      const result = await evoWebhookConfigService.delete(id);
      if (result.success) {
        navigate('/evo-webhooks');
        return;
      }
      setDeleteOpen(false);
      setDangerError(result.error || 'Erro ao excluir cadastro');
    } catch (error) {
      setDeleteOpen(false);
      setDangerError(errorMessage(error, 'Erro ao excluir cadastro'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyCallback = async () => {
    try {
      await navigator.clipboard.writeText(callbackUrl);
      notify('Endereço copiado.');
    } catch {
      notify('Não foi possível copiar. Selecione e copie o endereço manualmente.', 'warning');
    }
  };

  const handleDismissLeftover = async () => {
    setDismissing(true);
    setDismissError('');
    try {
      await evoPermissionCheckService.dismissLeftover(id);
      await reloadConfig();
    } catch (error) {
      setDismissError(errorMessage(error, 'Não foi possível remover o aviso. Tente de novo.'));
    } finally {
      setDismissing(false);
    }
  };

  const openConflictConfig = (targetId: string) => {
    setCredentialsOpen(false);
    navigate(`/evo-webhooks/${targetId}`);
  };

  const editCredentialsButton = (variant: 'outlined' | 'text') => (
    <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
      <Button variant={variant} onClick={() => setCredentialsOpen(true)} disabled={!canManage}>
        Editar credenciais
      </Button>
    </DisabledTooltip>
  );

  // ── Etapa 3: conteúdo ──────────────────────────────────────────────────────
  const eventsToCreateButtonLabel = missingCount === 3 ? 'Criar webhooks na EVO' : `Criar o que falta (${missingCount})`;

  const step3Body = () => {
    if (missingPrerequisites.length > 0) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Para criar os webhooks, falta:
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', p: 0, my: 1 }}>
            {missingPrerequisites.map((item) => (
              <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1, typography: 'body2', py: 0.25 }}>
                <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                {item}
              </Box>
            ))}
          </Box>
          <Button variant="contained" disabled>
            Criar webhooks na EVO
          </Button>
        </Box>
      );
    }

    if (listLoading || listStatus === 'idle') {
      return (
        <Box aria-busy="true" aria-label={EVO_CHECKING_HINT} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[0, 1, 2, 3].map((row) => (
            <Skeleton key={row} variant="rounded" height={32} />
          ))}
        </Box>
      );
    }

    if (listFailed) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
          <Alert severity="error" sx={{ width: '100%' }}>
            Não foi possível consultar os webhooks na EVO. Confira o DNS e o token (e se ele é de integração nativa) nas credenciais e tente de novo.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchRemoteWebhooks(config)}>
              Tentar novamente
            </Button>
            {editCredentialsButton('text')}
          </Box>
        </Box>
      );
    }

    const canCreate = missingCount > 0 && !hasDuplicates;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {createFeedback && (
          <Alert severity={createFeedback.severity} onClose={() => setCreateFeedback(null)}>
            {createFeedback.message}
          </Alert>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Endereço de callback (o mesmo nos 3 webhooks)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2, pr: 0.5, py: 0.5, borderRadius: 2, bgcolor: 'background.default' }}>
            <Typography sx={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
              {callbackUrl}
            </Typography>
            <IconButton aria-label="Copiar endereço" onClick={handleCopyCallback}>
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </Box>

        {hasDuplicates && (
          <Alert
            severity="warning"
            action={
              <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                <Button color="inherit" size="small" variant="outlined" onClick={() => setRemoveOpen(true)} disabled={!canManage}>
                  Remover webhooks
                </Button>
              </DisabledTooltip>
            }
          >
            Há {analysis.duplicateEvents.map((d) => `${d.count} webhooks ${d.eventType}`).join(' e ')} para esta empresa. O Reedy pode receber a
            mesma venda duas vezes. Remova os webhooks Reedy e crie de novo.
          </Alert>
        )}
        {!hasDuplicates && missingCount === 3 && (
          <Alert severity="info">Nenhum webhook Reedy criado para esta empresa na EVO.</Alert>
        )}
        {!hasDuplicates && missingCount > 0 && missingCount < 3 && (
          <Alert severity="warning">
            {missingCount === 1 ? 'Falta 1 webhook.' : `Faltam ${missingCount} webhooks.`} Crie só o que falta: os que já existem não são alterados.
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Evento</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>ID na EVO</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analysis.rows.map((row, index) => (
                <TableRow key={`${row.eventType}-${row.id ?? 'faltando'}-${index}`}>
                  <TableCell sx={{ fontWeight: 500 }}>{row.eventType}</TableCell>
                  <TableCell>
                    <StatusChip {...rowStatusChip[row.status]} />
                  </TableCell>
                  <TableCell sx={{ color: row.id ? 'inherit' : 'text.secondary' }}>{row.id ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {canCreate && (
            <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
              <Button
                variant="contained"
                onClick={() => setCreateOpen(true)}
                disabled={!canManage || creating}
                startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {creating ? 'Criando…' : eventsToCreateButtonLabel}
              </Button>
            </DisabledTooltip>
          )}
          <Button startIcon={<RefreshIcon />} onClick={() => fetchRemoteWebhooks(config)} disabled={creating}>
            Atualizar lista
          </Button>
        </Box>
      </Box>
    );
  };

  // ── Remover e excluir ──────────────────────────────────────────────────────
  const removeDescription = listFailed
    ? EVO_CHECK_FAILED_HINT
    : listLoading
      ? EVO_CHECKING_HINT
      : hasReedyWebhooks
        ? `Apaga ${analysis.reedy.length === 1 ? 'o webhook' : `os ${analysis.reedy.length} webhooks`} desta empresa na EVO. O cadastro continua, mas as vendas deixam de chegar ao Reedy até os webhooks serem recriados.`
        : 'Não há webhooks Reedy criados para este cadastro.';
  const removeDisabled = !canManage || removing || !hasReedyWebhooks || listLoading || listFailed;

  const deleteDescription = listFailed
    ? EVO_CHECK_FAILED_HINT
    : listLoading
      ? EVO_CHECKING_HINT
      : hasReedyWebhooks
        ? 'Só é possível depois de remover os webhooks Reedy.'
        : 'Apaga o cadastro e o vínculo com a empresa. Não afeta nada na EVO.';
  const deleteDisabled = !canManage || deleting || hasReedyWebhooks || listLoading || listFailed;

  const eventsToCreate: EvoReedyEventType[] = analysis.missingEvents;

  return (
    <Box p={{ xs: 1, sm: 3 }}>
      <Box sx={{ maxWidth: 880 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="Navegação" sx={{ mb: 1 }}>
          <Link component="button" type="button" underline="hover" onClick={() => navigate('/evo-webhooks')} sx={{ fontSize: 'inherit' }}>
            Webhooks EVO
          </Link>
          <Typography variant="body2" color="text.primary" sx={{ overflowWrap: 'anywhere' }}>
            {config.name}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Box sx={{ flex: '1 1 400px', minWidth: 0 }}>
            <Typography variant="h4" component="h1" sx={{ overflowWrap: 'anywhere' }}>
              {config.name}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              <StatusChip {...generalChip} />
              {nativeToken && <StatusChip label="Integração nativa" tone="info" />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, overflowWrap: 'anywhere' }}>
              {[notes || 'Sem observações', createdAtLabel ? `Criado em ${createdAtLabel}` : ''].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {projectId === 'reedy-staging-b4ef1' && (
              <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                <Button color="warning" variant="outlined" onClick={() => setDevModeOpen(true)} disabled={!canManage}>
                  Simular venda EVO
                </Button>
              </DisabledTooltip>
            )}
            <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
              <Button startIcon={<EditIcon />} onClick={() => setProfileOpen(true)} disabled={!canManage}>
                Editar nome e observações
              </Button>
            </DisabledTooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Etapa 1: credenciais */}
          <StepSection
            number={1}
            state={step1State}
            title="Credenciais da conta EVO"
            chip={<StatusChip label={hasCredentials ? 'Configuradas' : 'Pendente'} tone={hasCredentials ? 'ok' : 'neutral'} />}
          >
            {hasCredentials ? (
              <>
                <KeyValueList>
                  <KeyValue label="Conta EVO (DNS)">{maskDns(config.dns as string)}</KeyValue>
                  <KeyValue label="Token">Cadastrado (oculto)</KeyValue>
                  <KeyValue label="Tipo do token">
                    {nativeToken ? <StatusChip label="Integração nativa" tone="info" /> : <StatusChip label="Token comum" tone="neutral" />}
                  </KeyValue>
                  <KeyValue label="Permissões do token">
                    <StatusChip label={checkState.label} tone={checkState.tone} />
                  </KeyValue>
                </KeyValueList>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
                  {checkState.detail}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                  <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                    <Button variant="outlined" onClick={() => setCredentialsOpen(true)} disabled={!canManage || credentialsLocked}>
                      Editar credenciais
                    </Button>
                  </DisabledTooltip>
                  {/* O teste cria e apaga um webhook de teste, então não depende da trava de credenciais. */}
                  <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                    <Button variant="outlined" onClick={() => setCheckOpen(true)} disabled={!canManage}>
                      {checkState.kind === 'undefined' ? 'Testar permissões' : 'Testar de novo'}
                    </Button>
                  </DisabledTooltip>
                  {checkState.hasResult && (
                    <Button onClick={() => setResultToggle(!resultOpen)} aria-expanded={resultOpen}>
                      {resultOpen ? 'Ocultar resultado' : 'Ver resultado'}
                    </Button>
                  )}
                  {credentialsLocked && <StepHint>DNS, token e tipo do token ficam travados enquanto houver webhooks Reedy.</StepHint>}
                </Box>
                {!canManage ? (
                  <Box sx={{ mt: 1 }}>
                    <StepHint>{NO_PERMISSION_HINT}</StepHint>
                  </Box>
                ) : (
                  checkState.kind === 'undefined' && (
                    <Box sx={{ mt: 1 }}>
                      <StepHint>Chama na EVO tudo o que o Reedy usa e cria e apaga um webhook de teste. O resultado fica salvo no cadastro.</StepHint>
                    </Box>
                  )
                )}
                {checkState.hasResult && (
                  <Collapse in={resultOpen} unmountOnExit>
                    <Box sx={{ mt: 2 }}>
                      <PermissionCheckStepList rows={savedCheckRows} simulated={config.permissionCheck?.simulated === true} showRoutes />
                    </Box>
                  </Collapse>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2">
                  Informe o DNS e o token da conta EVO. Se o token foi gerado pela automação da EVO, marque <strong>Token de integração nativa</strong>.
                  As permissões do token são testadas ao salvar.
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                    <Button variant="contained" onClick={() => setCredentialsOpen(true)} disabled={!canManage}>
                      Cadastrar credenciais
                    </Button>
                  </DisabledTooltip>
                  <DisabledTooltip title="">
                    <Button variant="outlined" disabled>
                      Testar permissões
                    </Button>
                  </DisabledTooltip>
                  <StepHint>Cadastre as credenciais para poder testar.</StepHint>
                </Box>
              </>
            )}

            {leftoverNotice && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>{leftoverNotice.title}</AlertTitle>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {leftoverNotice.body}
                </Typography>
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {leftoverNotice.copyActions.map((action) => (
                    <CopyButton key={action.label} text={action.text} label={action.label} />
                  ))}
                  <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={handleDismissLeftover}
                      disabled={!canManage || dismissing}
                      startIcon={dismissing ? <CircularProgress size={14} color="inherit" /> : undefined}
                    >
                      {dismissing ? 'Removendo…' : 'Já removi'}
                    </Button>
                  </DisabledTooltip>
                </Box>
                {leftoverNotice.registeredAt && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {leftoverNotice.registeredAt}
                  </Typography>
                )}
                {!canManage && (
                  <Box sx={{ mt: 1 }}>
                    <StepHint>{NO_PERMISSION_HINT}</StepHint>
                  </Box>
                )}
                {dismissError && (
                  <Typography variant="body2" color="error" role="alert" sx={{ mt: 1 }}>
                    {dismissError}
                  </Typography>
                )}
              </Alert>
            )}
          </StepSection>

          {/* Etapa 2: empresa */}
          <StepSection
            number={2}
            state={step2State}
            title="Empresa vinculada"
            chip={
              <StatusChip
                label={linkedCompanyUnknown ? 'Revisar' : hasCompany ? 'Vinculada' : 'Pendente'}
                tone={linkedCompanyUnknown ? 'warning' : hasCompany ? 'ok' : 'neutral'}
              />
            }
          >
            {!companyEditing && (
              <KeyValueList>
                <KeyValue label="Empresa">{companyLabel}</KeyValue>
              </KeyValueList>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: companyEditing ? 0 : 1 }}>
              O webhook usa esta empresa para conceder títulos. Cada empresa fica em um único cadastro.
            </Typography>
            {linkedCompanyUnknown && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Este cadastro aponta para uma empresa que não aparece na lista (removida ou ID incorreto). Escolha outra empresa ou remova o vínculo.
              </Alert>
            )}
            {companyLinkError && (
              <Alert
                severity={companyLinkError.severity}
                sx={{ mt: 2 }}
                action={
                  companyLinkError.conflictingWebhookConfigId && companyLinkError.conflictingWebhookConfigId !== id ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => navigate(`/evo-webhooks/${companyLinkError.conflictingWebhookConfigId}`)}
                    >
                      Abrir cadastro
                    </Button>
                  ) : undefined
                }
              >
                {companyLinkError.message}
              </Alert>
            )}
            {companyEditing ? (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 240, flex: '1 1 240px', maxWidth: 420 }} disabled={Boolean(companyChangeBlockedReason)}>
                  <InputLabel id="evo-config-company-label" shrink>
                    Empresa
                  </InputLabel>
                  <Select
                    labelId="evo-config-company-label"
                    label="Empresa"
                    displayEmpty
                    notched
                    value={companyLinkDraft}
                    onChange={(e) => setCompanyLinkDraft(typeof e.target.value === 'string' ? e.target.value : '')}
                  >
                    <MenuItem value="">
                      <em>{hasCompany ? 'Nenhuma' : 'Selecione uma empresa'}</em>
                    </MenuItem>
                    {linkedCompanyUnknown && config.companyId && (
                      <MenuItem value={config.companyId}>ID não listado ({config.companyId.slice(0, 8)}…)</MenuItem>
                    )}
                    {companiesForSelect.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                        {!company.isActive ? ' (inativa)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                  <Button
                    variant="contained"
                    onClick={handleSaveCompanyLink}
                    disabled={!canManage || !companyLinkDirty || savingCompanyLink || Boolean(companyChangeBlockedReason)}
                  >
                    {savingCompanyLink ? 'Salvando…' : 'Salvar vínculo'}
                  </Button>
                </DisabledTooltip>
                {changingCompany && !linkedCompanyUnknown && (
                  <Button
                    onClick={() => {
                      setChangingCompany(false);
                      setCompanyLinkDraft(config.companyId || '');
                      setCompanyLinkError(null);
                    }}
                    disabled={savingCompanyLink}
                  >
                    Cancelar
                  </Button>
                )}
                {companyChangeBlockedReason && <StepHint>{companyChangeBlockedReason}</StepHint>}
              </Box>
            ) : (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                  <Button
                    variant="outlined"
                    onClick={() => setChangingCompany(true)}
                    disabled={!canManage || Boolean(companyChangeBlockedReason)}
                  >
                    Alterar empresa
                  </Button>
                </DisabledTooltip>
                {companyChangeBlockedReason && <StepHint>{companyChangeBlockedReason}</StepHint>}
              </Box>
            )}
          </StepSection>

          {/* Etapa 3: webhooks na EVO */}
          <StepSection number={3} state={step3State} title="Webhooks na EVO" chip={<StatusChip {...step3Chip} />}>
            {step3Body()}
          </StepSection>

          {/* Remover e excluir */}
          <Paper component="section" aria-labelledby="evo-danger-title" sx={{ px: { xs: 2, sm: 3 }, py: 1 }}>
            <Typography id="evo-danger-title" variant="h6" component="h2" sx={{ pt: 2 }}>
              Remover e excluir
            </Typography>
            {dangerError && (
              <Alert severity="error" sx={{ mt: 1 }} onClose={() => setDangerError('')}>
                {dangerError}
              </Alert>
            )}
            <Box
              sx={{
                py: 2,
                display: 'flex',
                gap: 2,
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500 }}>Remover webhooks Reedy</Typography>
                <Typography variant="body2" color="text.secondary">
                  {removeDescription}
                </Typography>
              </Box>
              <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                <Button variant="outlined" color="error" onClick={() => setRemoveOpen(true)} disabled={removeDisabled} sx={{ flex: 'none' }}>
                  {removing ? 'Removendo…' : 'Remover webhooks'}
                </Button>
              </DisabledTooltip>
            </Box>
            <Box
              sx={{
                py: 2,
                display: 'flex',
                gap: 2,
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500 }}>Excluir cadastro</Typography>
                <Typography variant="body2" color="text.secondary">
                  {deleteDescription}
                </Typography>
              </Box>
              <DisabledTooltip title={canManage ? '' : NO_PERMISSION_HINT}>
                <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)} disabled={deleteDisabled} sx={{ flex: 'none' }}>
                  Excluir cadastro
                </Button>
              </DisabledTooltip>
            </Box>
          </Paper>
        </Box>
      </Box>

      <EvoWebhookCredentialsDialog
        open={credentialsOpen}
        initial={hasCredentials ? { dns: config.dns as string, token: config.token as string, nativeIntegration: nativeToken } : null}
        currentConfigId={id}
        onClose={() => setCredentialsOpen(false)}
        onSubmit={submitCredentials}
        onOpenConflict={openConflictConfig}
        onReloadConfig={reloadConfig}
      />

      <EvoPermissionCheckDialog
        open={checkOpen}
        configId={id}
        mode="saved"
        onDone={() => {
          setCheckOpen(false);
          void reloadConfig();
        }}
      />

      <EvoWebhookProfileDialog
        open={profileOpen}
        name={config.name}
        notes={config.notes || ''}
        onClose={() => setProfileOpen(false)}
        onSubmit={submitProfile}
      />

      <EvoWebhookCreateDialog
        open={createOpen}
        companyName={companyLabel}
        configName={config.name}
        eventsToCreate={eventsToCreate}
        existingEvents={analysis.createdEvents}
        nativeIntegration={nativeToken}
        creating={creating}
        onCancel={() => setCreateOpen(false)}
        onConfirm={handleConfirmCreate}
      />

      <DestructiveActionDialog
        open={removeOpen}
        title="Remover os webhooks Reedy?"
        description="Todos os webhooks Reedy desta empresa serão apagados na EVO. Até serem recriados, as vendas deixam de chegar ao Reedy. O cadastro continua."
        confirmLabel="Remover webhooks"
        busy={removing}
        onCancel={() => setRemoveOpen(false)}
        onConfirm={handleConfirmRemove}
      />

      <DestructiveActionDialog
        open={deleteOpen}
        entityLabel="cadastro"
        entityName={config.name}
        busy={deleting}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {/* Dev Mode Panel (staging only) */}
      {projectId === 'reedy-staging-b4ef1' && (
        <DevModePanel
          open={devModeOpen}
          onClose={() => setDevModeOpen(false)}
          companyDns={config.dns || ''}
          companyToken={config.token || ''}
          companyId={config.companyId || ''}
        />
      )}

      {/* Snackbar só para sucesso e avisos; erros ficam no ponto da ação */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvoWebhookDetailPage;
